/**
 * DecisionLedger — the governance moat, made felt.
 *
 * Argo's one defensible differentiator is that every governed change is
 * recorded in an append-only, hash-chained, tamper-evident ledger. That data
 * already existed (src/lib/db/activity.ts) but was only ever a small feed card
 * on the Dashboard. This surfaces the whole thing as a first-class audit
 * surface: the full Decision Ledger, newest-first, each event provenance-
 * stamped (agent vs human, via the SAME deriveActor rule the Time-Machine
 * uses — no drift), sealed with its chain hash, filterable by event type,
 * one-click chain verification, and a one-click auditor CSV export.
 *
 * Honest by contract: entries are never seeded — an empty ledger means nothing
 * governed has happened yet. Verification reports the real chain state,
 * pinpointing the first broken entry if the chain is ever tampered with.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  History,
  ShieldCheck,
  ShieldAlert,
  Download,
  FileJson,
  Bot,
  User,
  Lock,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import {
  subscribeActivity,
  verifyLedger,
  exportLedger,
} from '../lib/db/activity';
import {
  exportLedger as exportSealedLedger,
  serializeExport,
} from '../lib/ledger/exportLedger';
import { toLedgerEntries } from '../lib/ledger/verifyChain';
import type { ActivityEntry } from '../lib/db/types';
import { deriveActor, ledgerProvenance } from '../lib/crm/timeMachine';

type EventFilter = 'all' | ActivityEntry['event'];

/** Event → accent + label, mirroring the Dashboard's governed-activity palette. */
const EVENT_META: Record<ActivityEntry['event'], { label: string; accent: string }> = {
  proposed: { label: 'Proposed', accent: C.orange },
  approved: { label: 'Approved', accent: C.green },
  executed: { label: 'Executed', accent: C.blueElectric },
  rejected: { label: 'Rejected', accent: C.orangeWarm },
  undone: { label: 'Undone', accent: C.textMuted },
  agent_run: { label: 'Agent run', accent: C.magentaSoft },
};

const FILTER_ORDER: EventFilter[] = [
  'all',
  'proposed',
  'approved',
  'executed',
  'rejected',
  'undone',
  'agent_run',
];

type VerifyState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'ok'; sealed: number }
  | { status: 'broken'; brokenAt: string };

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DecisionLedger() {
  const [rows, setRows] = useState<ActivityEntry[]>([]);
  const [filter, setFilter] = useState<EventFilter>('all');
  const [verify, setVerify] = useState<VerifyState>({ status: 'idle' });

  useEffect(() => {
    // subscribeActivity already emits newest-first.
    const unsub = subscribeActivity(setRows);
    return unsub;
  }, []);

  const prov = useMemo(() => ledgerProvenance(rows), [rows]);
  const sealedCount = useMemo(
    () => rows.filter((r) => typeof r.hash === 'string' && r.hash !== '').length,
    [rows],
  );

  const counts = useMemo(() => {
    const acc: Record<EventFilter, number> = {
      all: rows.length,
      proposed: 0,
      approved: 0,
      executed: 0,
      rejected: 0,
      undone: 0,
      agent_run: 0,
    };
    for (const r of rows) acc[r.event]++;
    return acc;
  }, [rows]);

  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.event === filter)),
    [rows, filter],
  );

  async function onVerify() {
    setVerify({ status: 'checking' });
    try {
      const res = await verifyLedger();
      setVerify(
        res.ok
          ? { status: 'ok', sealed: sealedCount }
          : { status: 'broken', brokenAt: res.brokenAt ?? 'unknown' },
      );
    } catch {
      setVerify({ status: 'broken', brokenAt: 'verification error' });
    }
  }

  function downloadFile(content: string, mime: string, filename: string) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function onExport() {
    try {
      const csv = await exportLedger();
      downloadFile(
        csv,
        'text/csv;charset=utf-8',
        `argo-decision-ledger-${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } catch {
      /* export is read-only + best-effort; a failure must not break the view */
    }
  }

  /**
   * Portable sealed export — the forwardable audit artifact an auditor can
   * verify OUTSIDE the app with scripts/verify-ledger.mjs (zero deps).
   * Pure over the rows already subscribed here: sealed rows → abstract chain
   * (toLedgerEntries) → SealedLedgerExport → pretty canonical JSON download.
   */
  function onExportSealed() {
    try {
      const exp = exportSealedLedger(toLedgerEntries([...rows]), {
        tenant: 'argo',
        exportedAt: Date.now(),
      });
      downloadFile(
        serializeExport(exp),
        'application/json;charset=utf-8',
        `argo-sealed-ledger-${new Date().toISOString().slice(0, 10)}.json`,
      );
    } catch {
      /* export is read-only + best-effort; a failure must not break the view */
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Provenance header: the trust KPIs + the two governance actions. */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: 16 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22 }}>
            <Kpi label="Governed events" value={String(prov.totalEvents)} />
            <Kpi
              label="By agent · human"
              value={`${prov.byActor.agent} · ${prov.byActor.human}`}
            />
            <Kpi
              label="Tamper-evident"
              value={`${sealedCount}/${rows.length} sealed`}
            />
            <Kpi
              label="Last activity"
              value={prov.lastTouched ? formatWhen(prov.lastTouched) : '—'}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="glass-btn"
              onClick={onVerify}
              disabled={verify.status === 'checking' || rows.length === 0}
            >
              <ShieldCheck size={14} strokeWidth={2} />
              {verify.status === 'checking' ? 'Verifying…' : 'Verify chain'}
            </button>
            <button
              type="button"
              className="glass-btn"
              onClick={onExport}
              disabled={rows.length === 0}
            >
              <Download size={14} strokeWidth={2} />
              Export CSV
            </button>
            <button
              type="button"
              className="glass-btn"
              onClick={onExportSealed}
              disabled={rows.length === 0}
              title="Download the hash-chained ledger as a portable JSON artifact, independently verifiable with scripts/verify-ledger.mjs"
            >
              <FileJson size={14} strokeWidth={2} />
              Export sealed ledger
            </button>
          </div>
        </div>

        {verify.status === 'ok' && (
          <VerifyBanner
            ok
            text={`Chain intact — ${verify.sealed} sealed ${verify.sealed === 1 ? 'entry' : 'entries'} verified against their hashes.`}
          />
        )}
        {verify.status === 'broken' && (
          <VerifyBanner
            ok={false}
            text={`Chain integrity check FAILED at entry ${verify.brokenAt}. An entry was altered or spliced.`}
          />
        )}
      </GlassPanel>

      {/* Event-type filter chips (reuses the kanban phase-filter styling). */}
      <div className="kanban-toolbar">
        <div className="kanban-phasefilter" role="tablist">
          {FILTER_ORDER.map((f) => {
            const active = filter === f;
            const accent = f === 'all' ? C.blueElectric : EVENT_META[f].accent;
            const label = f === 'all' ? 'All' : EVENT_META[f].label;
            return (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={active}
                className="kanban-phasefilter__btn"
                data-active={active}
                style={
                  {
                    ['--phase-accent' as any]: accent,
                    ['--phase-accent-soft' as any]: 'rgba(255,255,255,0.18)',
                  } as React.CSSProperties
                }
                onClick={() => setFilter(f)}
              >
                <span className="kanban-phasefilter__dot" style={{ background: accent }} />
                <span>{label}</span>
                <span className="kanban-phasefilter__count">{counts[f]}</span>
              </button>
            );
          })}
        </div>
      </div>

      <GlassPanel variant="subtle" animate={false} style={{ padding: 0, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div className="crm-empty">
            <History size={20} strokeWidth={1.75} />
            <span>
              {rows.length === 0
                ? 'No governed activity yet. Every proposal, approval, execution, and undo an agent or operator makes will appear here — provenance-stamped and tamper-evident.'
                : 'Nothing matches this filter.'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'grid' }}>
            {filtered.map((r) => (
              <LedgerRow key={r.id} entry={r} />
            ))}
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <span className="t-meta" style={{ fontSize: 10.5, letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span style={{ fontSize: 17, fontWeight: 650, color: C.text, lineHeight: 1.1 }}>
        {value}
      </span>
    </div>
  );
}

function VerifyBanner({ ok, text }: { ok: boolean; text: string }) {
  const accent = ok ? C.green : C.orangeWarm;
  return (
    <div
      role="status"
      style={{
        marginTop: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '9px 12px',
        borderRadius: 10,
        border: `1px solid ${accent}55`,
        background: `${accent}14`,
        color: C.text,
        fontSize: 12.5,
      }}
    >
      {ok ? (
        <ShieldCheck size={15} strokeWidth={2} style={{ color: accent, flexShrink: 0 }} />
      ) : (
        <ShieldAlert size={15} strokeWidth={2} style={{ color: accent, flexShrink: 0 }} />
      )}
      <span>{text}</span>
    </div>
  );
}

function LedgerRow({ entry }: { entry: ActivityEntry }) {
  const meta = EVENT_META[entry.event];
  const actor = deriveActor(entry);
  const sealed = typeof entry.hash === 'string' && entry.hash !== '';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <span
        className="glass-pill"
        style={{ color: meta.accent, borderColor: `${meta.accent}55`, flexShrink: 0 }}
      >
        {meta.label}
      </span>
      <span
        className="glass-pill"
        title={actor === 'agent' ? 'Performed by an agent' : 'Performed / initiated by a human'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
      >
        {actor === 'agent' ? <Bot size={12} strokeWidth={2} /> : <User size={12} strokeWidth={2} />}
        {actor === 'agent' ? 'Agent' : 'Human'}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: C.text,
          fontSize: 13,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={entry.summary}
      >
        {entry.summary}
      </span>
      {sealed && (
        <span
          title={`Sealed · hash ${entry.hash}`}
          aria-label="Tamper-evident: this entry is sealed in the hash chain"
          style={{ display: 'inline-flex', alignItems: 'center', color: C.textDim, flexShrink: 0 }}
        >
          <Lock size={12} strokeWidth={2} />
        </span>
      )}
      <span className="t-meta" style={{ flexShrink: 0, fontSize: 11 }}>
        {formatWhen(entry.at)}
      </span>
    </div>
  );
}
